import { nextTick } from 'vue'

type ElementLookup = () => HTMLElement | null | undefined

// Focus targets set by one component and found by another. Each is a purpose-built
// attribute, exported here and bound by its owner, so a re-markup of the owner cannot
// silently leave the lookup matching nothing (a data-testid could). AB#65420
//
// The "review your order" note (CheckoutSubmit on desktop, MobileOrderSummary on a phone),
// focused by CheckoutBase once the last checkout step saves.
export const CHECKOUT_REVIEW_ATTR = 'data-checkout-review'
// A demonstrator finder result (DemonstratorList), focused once a search renders.
export const DEMONSTRATOR_RESULT_ATTR = 'data-focus-demonstrator-result'

// Lost means on the body, or on a control a step hid with v-show: the browser only blurs
// it on its next render, so it can still be activeElement here.
const focusIsLost = (): boolean => {
  const active = document.activeElement
  return !active || active === document.body || !active.checkVisibility()
}

// Waits for the DOM update, then focuses what `lookup` returns, only when focus has been
// lost, so a control that already holds focus keeps it. The lookup runs after the update,
// so a caller cannot hand in an element that is about to unmount. Resolves to whether it
// moved focus. Ported from nuxt2's utils/focus.ts (PR 23563). AB#65420
export const focusIfLost = async (lookup: ElementLookup, options?: FocusOptions): Promise<boolean> => {
  await nextTick()
  const el = lookup()
  if (!el || !focusIsLost()) { return false }
  el.focus(options)
  return true
}

// The first of `selector`'s matches that is rendered: a target that renders once per
// breakpoint can leave a hidden copy behind, and focus() on it does nothing.
export const firstShown = (selector: string, scope: ParentNode = document): HTMLElement | null =>
  Array.from(scope.querySelectorAll<HTMLElement>(selector)).find(el => el.checkVisibility()) ?? null

export const firstDemonstratorResult = (scope: ParentNode): HTMLElement | null =>
  scope.querySelector<HTMLElement>(`[${DEMONSTRATOR_RESULT_ATTR}]`)

// A demonstrator finder's search swaps its form for the results, unmounting the focused
// field or Search button. Once rendered, land on what `target` returns for the finder's
// root: its first result, or its no-matches message. Focus still inside the finder (a
// Search button that stays up beside no-matches) moves too; focus the shopper took
// elsewhere while the search ran is left alone. AB#65420
export const focusSearchOutcome = async (
  root: ElementLookup,
  target: (finder: HTMLElement) => HTMLElement | null | undefined
): Promise<boolean> => {
  await nextTick()
  const finder = root()
  const active = document.activeElement
  if (!finder || (active && active !== document.body && !finder.contains(active))) { return false }
  const el = target(finder)
  if (!el) { return false }
  el.focus()
  return true
}

// Whether this page was reached by client-side navigation rather than a hard load, which
// must keep the skip link as its first Tab stop. A hard load is still hydrating while its
// components set up and mount: Nuxt clears isHydrating only once the page's mounted hooks
// have run. Read it during setup. AB#65420
export const isClientArrival = (): boolean => import.meta.client && !useNuxtApp().isHydrating
